import { Injectable, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { TranslationService } from './translation.service';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class SeoService {
  private titleService = inject(Title);
  private metaService = inject(Meta);
  private translationService = inject(TranslationService);
  private document = inject(DOCUMENT);

  // Schema.org Structured Data
  private schemaData = {
    '@context': 'https://schema.org',
    '@type': 'GeneralContractor',
    'name': 'Milok Gerüstbau',
    'url': 'https://milok.eu',
    'logo': 'https://milok.eu/assets/images/logo.jpg',
    'image': 'https://milok.eu/assets/images/header-bg.jpg',
    'description': 'Ihr Partner für professionellen Gerüstbau und Karriere in Deutschland. Milok Gerüstbau steht für Sicherheit und Qualität.',
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': 'DE'
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': '51.1657', // General Germany coordinates
      'longitude': '10.4515'
    },
    'telephone': '+49123456789', // Placeholder
    'openingHoursSpecification': {
      '@type': 'OpeningHoursSpecification',
      'dayOfWeek': [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday'
      ],
      'opens': '08:00',
      'closes': '17:00'
    },
    'sameAs': [
      'https://www.facebook.com/milokgeruestbau'
    ]
  };

  constructor() {
    this.injectSchema();
  }

  updateMetaTags() {
    const t = this.translationService.t();
    
    // 1. Update Title
    this.titleService.setTitle(t.seo.title);

    // 2. Update Standard Meta Tags
    this.metaService.updateTag({ name: 'description', content: t.seo.description });
    this.metaService.updateTag({ name: 'keywords', content: t.seo.keywords });
    this.metaService.updateTag({ name: 'robots', content: 'index, follow' });
    this.metaService.updateTag({ name: 'author', content: 'Milok Gerüstbau' });

    // 3. Update Open Graph (Facebook, WhatsApp, LinkedIn)
    this.metaService.updateTag({ property: 'og:title', content: t.seo.title });
    this.metaService.updateTag({ property: 'og:description', content: t.seo.description });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ property: 'og:url', content: 'https://milok.eu' });
    this.metaService.updateTag({ property: 'og:image', content: 'https://milok.eu/assets/images/header-bg.jpg' });
    this.metaService.updateTag({ property: 'og:site_name', content: 'Milok Gerüstbau' });
    this.metaService.updateTag({ property: 'og:locale', content: this.getLocaleCode() });

    // 4. Update Twitter Card
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: t.seo.title });
    this.metaService.updateTag({ name: 'twitter:description', content: t.seo.description });
    this.metaService.updateTag({ name: 'twitter:image', content: 'https://milok.eu/assets/images/header-bg.jpg' });

    // 5. Update HTML lang attribute
    this.document.documentElement.lang = this.translationService.currentLangCode();
  }

  private getLocaleCode(): string {
    const lang = this.translationService.currentLangCode();
    switch (lang) {
      case 'sk': return 'sk_SK';
      case 'de': return 'de_DE';
      case 'en': return 'en_US';
      default: return 'de_DE';
    }
  }

  private injectSchema() {
    const script = this.document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(this.schemaData);
    this.document.head.appendChild(script);
  }
}
